/**
 * UpdateDownloadManager - Web/Electron version
 * Handles downloading update files for web and Electron platforms
 */

import { IUpdateComponent, IUpdateProgress } from '../models/Update';
import UpdateService from './updateService';

const OTA_LOG_PREFIX = '[OTA DownloadManager]';

export type DownloadProgressCallback = (progress: IUpdateProgress) => void;

function parseSha256Checksum(expectedChecksum: string): string | null {
  if (!expectedChecksum) return null;
  const value = expectedChecksum.trim();
  if (!value.toLowerCase().startsWith('sha256:')) return null;
  const hex = value.slice('sha256:'.length).trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(hex)) return null;
  return hex;
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  // WebCrypto is available in modern browsers and Electron renderer.
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('SHA-256 unavailable (crypto.subtle missing)');
  }

  const digest = await subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export class UpdateDownloadManager {
  private static instance: UpdateDownloadManager;
  private activeDownloads: Map<string, AbortController> = new Map();
  private progressCallbacks: Map<string, DownloadProgressCallback> = new Map();

  private constructor() {}

  static getInstance(): UpdateDownloadManager {
    if (!UpdateDownloadManager.instance) {
      UpdateDownloadManager.instance = new UpdateDownloadManager();
    }
    return UpdateDownloadManager.instance;
  }

  /**
   * Download an update component
   */
  async downloadUpdate(
    update: IUpdateComponent,
    onProgress?: DownloadProgressCallback
  ): Promise<string> {
    const downloadId = `${update.type}_${update.version}`;

    console.log(`${OTA_LOG_PREFIX} Starting download for ${downloadId}`);
    console.log(`${OTA_LOG_PREFIX} Update details:`, JSON.stringify({
      type: update.type,
      version: update.version,
      url: update.url,
      size: update.size,
      checksum: update.checksum,
      mandatory: update.mandatory,
    }, null, 2));

    if (this.activeDownloads.has(downloadId)) {
      console.warn(`${OTA_LOG_PREFIX} Download already in progress for ${downloadId}`);
      throw new Error(`Download already in progress for ${downloadId}`);
    }

    if (onProgress) {
      this.progressCallbacks.set(downloadId, onProgress);
    }

    const electronAPI = typeof window !== 'undefined' && (window as any).electronAPI;
    console.log(`${OTA_LOG_PREFIX} Electron API available: ${!!electronAPI}`);

    // Enforce HTTPS for OTA downloads.
    try {
      const parsedUrl = new URL(update.url);
      console.log(`${OTA_LOG_PREFIX} URL protocol: ${parsedUrl.protocol}`);
      if (parsedUrl.protocol !== 'https:') {
        console.error(`${OTA_LOG_PREFIX} HTTPS required but got ${parsedUrl.protocol}`);
        throw new Error('Update download must use HTTPS');
      }
    } catch (e) {
      console.error(`${OTA_LOG_PREFIX} Invalid URL:`, e);
      throw new Error('Invalid update URL');
    }

    // Use main-process streaming downloads on Electron to avoid buffering large artifacts in memory.
    if (electronAPI) {
      console.log(`${OTA_LOG_PREFIX} Using Electron main process for download`);
      this.activeDownloads.set(downloadId, new AbortController());
      const fileName = `${update.type}_${update.version}${this.getFileExtension(update.type)}`;
      console.log(`${OTA_LOG_PREFIX} Target filename: ${fileName}`);
      let removeProgressListener: (() => void) | null = null;

      try {
        console.log(`${OTA_LOG_PREFIX} Notifying download start...`);
        this.notifyProgress(downloadId, {
          type: update.type,
          status: 'downloading',
          progress: 0,
        });

        removeProgressListener = electronAPI.onDownloadProgress?.((data: any) => {
          if (!data || data.downloadId !== downloadId) return;
          const totalBytes = Number(data.totalBytes || 0);
          const receivedBytes = Number(data.receivedBytes || 0);
          const progress =
            totalBytes > 0 ? Math.min(100, Math.round((receivedBytes / totalBytes) * 100)) : 0;

          if (progress % 10 === 0) {
            console.log(`${OTA_LOG_PREFIX} Download progress: ${progress}% (${receivedBytes}/${totalBytes} bytes)`);
          }

          this.notifyProgress(downloadId, {
            type: update.type,
            status: 'downloading',
            progress,
          });
        });

        console.log(`${OTA_LOG_PREFIX} Calling electronAPI.downloadUpdate...`);
        const downloadStartTime = Date.now();
        const filePath = await electronAPI.downloadUpdate({
          downloadId,
          url: update.url,
          fileName,
          expectedChecksum: update.checksum,
          expectedSize: update.size,
        });
        const downloadDuration = Date.now() - downloadStartTime;
        console.log(`${OTA_LOG_PREFIX} Download completed in ${downloadDuration}ms`);
        console.log(`${OTA_LOG_PREFIX} Downloaded to: ${filePath}`);

        this.notifyProgress(downloadId, {
          type: update.type,
          status: 'ready',
          progress: 100,
          downloadedPath: filePath,
        });

        console.log(`${OTA_LOG_PREFIX} Storing pending update...`);
        await UpdateService.storePendingUpdate(update, filePath);
        console.log(`${OTA_LOG_PREFIX} Download successful for ${downloadId}`);
        return filePath;
      } catch (error: any) {
        console.error(`${OTA_LOG_PREFIX} Download failed for ${downloadId}:`, error);
        this.notifyProgress(downloadId, {
          type: update.type,
          status: 'failed',
          progress: 0,
          error: error?.message || 'Download failed',
        });
        throw error;
      } finally {
        try {
          removeProgressListener?.();
        } catch {
          // ignore
        }
        this.activeDownloads.delete(downloadId);
        this.progressCallbacks.delete(downloadId);
        console.log(`${OTA_LOG_PREFIX} Cleaned up download state for ${downloadId}`);
      }
    }

    console.log(`${OTA_LOG_PREFIX} Using web fetch for download (non-Electron)`);
    const abortController = new AbortController();
    this.activeDownloads.set(downloadId, abortController);

    try {
      this.notifyProgress(downloadId, {
        type: update.type,
        status: 'downloading',
        progress: 0,
      });

      console.log(`${OTA_LOG_PREFIX} Fetching ${update.url}...`);
      const fetchStartTime = Date.now();
      const response = await fetch(update.url, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        console.error(`${OTA_LOG_PREFIX} HTTP error: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : update.size;
      console.log(`${OTA_LOG_PREFIX} Content-Length: ${contentLength}, total: ${total}`);

      const reader = response.body?.getReader();
      if (!reader) {
        console.error(`${OTA_LOG_PREFIX} Unable to get response body reader`);
        throw new Error('Unable to read response body');
      }

      const chunks: Uint8Array[] = [];
      let receivedLength = 0;
      let lastLoggedProgress = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        const progress = Math.round((receivedLength / total) * 100);
        if (progress >= lastLoggedProgress + 10) {
          console.log(`${OTA_LOG_PREFIX} Web download progress: ${progress}% (${receivedLength}/${total} bytes)`);
          lastLoggedProgress = progress;
        }
        this.notifyProgress(downloadId, {
          type: update.type,
          status: 'downloading',
          progress,
        });
      }

      const fetchDuration = Date.now() - fetchStartTime;
      console.log(`${OTA_LOG_PREFIX} Fetch completed in ${fetchDuration}ms, received ${receivedLength} bytes`);

      // Combine chunks
      const completeData = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        completeData.set(chunk, position);
        position += chunk.length;
      }

      // Verify checksum before persisting the update (web path)
      console.log(`${OTA_LOG_PREFIX} Verifying checksum...`);
      const expectedSha256 = parseSha256Checksum(update.checksum);
      if (!expectedSha256) {
        console.error(`${OTA_LOG_PREFIX} Invalid checksum format: ${update.checksum}`);
        throw new Error('Invalid checksum format (expected sha256:<64-hex>)');
      }
      const actualSha256 = await sha256Hex(completeData);
      console.log(`${OTA_LOG_PREFIX} Expected checksum: ${expectedSha256}`);
      console.log(`${OTA_LOG_PREFIX} Actual checksum: ${actualSha256}`);
      if (actualSha256 !== expectedSha256) {
        console.error(`${OTA_LOG_PREFIX} Checksum mismatch!`);
        throw new Error('Checksum verification failed');
      }
      console.log(`${OTA_LOG_PREFIX} Checksum verified successfully`);

      // For web, store in IndexedDB or trigger download
      const blob = new Blob([completeData]);
      const blobUrl = URL.createObjectURL(blob);
      console.log(`${OTA_LOG_PREFIX} Created blob URL: ${blobUrl}`);

      this.notifyProgress(downloadId, {
        type: update.type,
        status: 'ready',
        progress: 100,
        downloadedPath: blobUrl,
      });

      console.log(`${OTA_LOG_PREFIX} Web download successful for ${downloadId}`);
      return blobUrl;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log(`${OTA_LOG_PREFIX} Download cancelled for ${downloadId}`);
        this.notifyProgress(downloadId, {
          type: update.type,
          status: 'failed',
          progress: 0,
          error: 'Download cancelled',
        });
      } else {
        console.error(`${OTA_LOG_PREFIX} Web download failed for ${downloadId}:`, error);
        this.notifyProgress(downloadId, {
          type: update.type,
          status: 'failed',
          progress: 0,
          error: error.message || 'Download failed',
        });
      }
      throw error;
    } finally {
      this.activeDownloads.delete(downloadId);
      this.progressCallbacks.delete(downloadId);
      console.log(`${OTA_LOG_PREFIX} Cleaned up web download state for ${downloadId}`);
    }
  }

  /**
   * Cancel an active download
   */
  async cancelDownload(type: string, version: string): Promise<void> {
    const downloadId = `${type}_${version}`;
    console.log(`${OTA_LOG_PREFIX} Cancelling download: ${downloadId}`);

    const abortController = this.activeDownloads.get(downloadId);

    if (abortController) {
      console.log(`${OTA_LOG_PREFIX} Aborting local controller for ${downloadId}`);
      abortController.abort();
      this.activeDownloads.delete(downloadId);
      this.progressCallbacks.delete(downloadId);
    } else {
      console.log(`${OTA_LOG_PREFIX} No active local controller found for ${downloadId}`);
    }

    const electronAPI = typeof window !== 'undefined' && (window as any).electronAPI;
    if (electronAPI && typeof electronAPI.cancelUpdateDownload === 'function') {
      console.log(`${OTA_LOG_PREFIX} Cancelling Electron download: ${downloadId}`);
      await electronAPI.cancelUpdateDownload(downloadId);
      console.log(`${OTA_LOG_PREFIX} Electron download cancelled: ${downloadId}`);
    }
  }

  /**
   * Get file extension based on update type
   */
  private getFileExtension(type: string): string {
    switch (type) {
      case 'js_bundle':
        return '.bundle';
      case 'algo_code':
        return '.zip';
      case 'checkpoint':
        // Checkpoints are shipped as a zip to allow partial updates (multiple files).
        return '.zip';
      case 'ml_runtime_cpu':
      case 'ml_runtime_cuda':
        return '.zip';
      default:
        return '.dat';
    }
  }

  /**
   * Notify progress to registered callback
   */
  private notifyProgress(downloadId: string, progress: IUpdateProgress): void {
    const callback = this.progressCallbacks.get(downloadId);
    if (callback) {
      callback(progress);
    }
  }
}

export default UpdateDownloadManager.getInstance();
